import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'

import { GetAccount } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { logError } from '~publish/legacy/utils/logError'

/**
 * TODO: This will get refactored to use updated GraphQL mutation
 */
export const UpdateOrganizationNameMutation = graphql(/* GraphQL */ `
  mutation OrganizationSetName($organizationId: String!, $name: String!) {
    organizationSetName(organizationId: $organizationId, name: $name)
  }
`)

type UpdateOrganizationName = ({
  organizationId,
  name,
}: {
  organizationId: string
  name: string
}) => Promise<boolean>

type UpdateOrganizationNameResponse = {
  updateOrganizationName: UpdateOrganizationName
  isLoading: boolean
  hasError: boolean
}

/**
 * Custom hook to update the current organization name.
 * @returns {UpdateOrganizationNameResponse} The function to trigger updating the organization name.
 */
export const useUpdateOrganizationName = (): UpdateOrganizationNameResponse => {
  const [executeUpdateOrganizationName, { loading, error }] = useMutation(
    UpdateOrganizationNameMutation,
  )

  const updateOrganizationName = useCallback(
    async ({
      organizationId,
      name,
    }: {
      organizationId: string
      name: string
    }) => {
      try {
        const { data, error: mutationError } =
          await executeUpdateOrganizationName({
            variables: {
              organizationId,
              name,
            },
            refetchQueries: [GetAccount],
          })

        // Handle GraphQL errors if any
        if (mutationError) {
          const unexpectedError = new Error(
            mutationError.message || 'Unexpected API response',
          )
          logError(unexpectedError, {
            metaData: { data, error: mutationError },
          })
          throw unexpectedError
        }
        toast.success('Organization name updated')

        return Boolean(data?.organizationSetName)
      } catch (updateError) {
        toast.error(
          'An error occurred while updating your organization name. Please try again.',
        )
        logError(
          updateError instanceof Error
            ? updateError
            : new Error(String(updateError)),
          {
            metaData: { variables: { organizationId, name } },
          },
        )
        throw updateError
      }
    },
    [executeUpdateOrganizationName],
  )

  return {
    updateOrganizationName,
    isLoading: loading,
    hasError: !!error,
  }
}
